import React from 'react';
import { Link } from 'react-router-dom';
import { Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, cartTotal } = useCart();

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen pt-28 flex flex-col items-center justify-center text-center px-4">
        <ShoppingBag size={56} className="text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold text-[#111111] mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-6">Browse our granite collection and add products to your order.</p>
        <Link to="/products">
          <Button className="bg-[#111111] text-white hover:bg-[#2a2a2a]">Browse Products</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAFAF8]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-[#111111] mb-8">Your Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <Card key={item.cartId}>
                <CardContent className="p-5">
                  <div className="flex gap-4">
                    <img
                      src={item.image}
                      alt={item.productName}
                      className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <Link
                          to={`/products/${item.slug}`}
                          className="font-semibold text-[#111111] hover:underline text-sm leading-tight"
                        >
                          {item.productName}
                        </Link>
                        <button
                          onClick={() => removeFromCart(item.cartId)}
                          className="text-gray-400 hover:text-red-500 transition-colors ml-2 flex-shrink-0"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {item.finish} · {item.size} · {item.thickness}
                      </p>
                      <p className="text-xs text-gray-500">₹{item.pricePerSqFt} / sq ft</p>

                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.cartId, item.quantity - 10)}
                            className="w-7 h-7 rounded border border-gray-300 flex items-center justify-center text-sm hover:border-gray-500 transition"
                          >
                            −
                          </button>
                          <span className="w-12 text-center text-sm font-medium">{item.quantity} sq ft</span>
                          <button
                            onClick={() => updateQuantity(item.cartId, item.quantity + 10)}
                            className="w-7 h-7 rounded border border-gray-300 flex items-center justify-center text-sm hover:border-gray-500 transition"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-[#111111]">
                          ₹{(item.pricePerSqFt * item.quantity).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg text-[#111111] mb-4">Order Summary</h3>

                <div className="space-y-2 mb-4">
                  {cartItems.map((item) => (
                    <div key={item.cartId} className="flex justify-between text-sm text-gray-600">
                      <span className="truncate mr-2">{item.productName} ({item.quantity} sq ft)</span>
                      <span className="flex-shrink-0">₹{(item.pricePerSqFt * item.quantity).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-3 mb-6">
                  <div className="flex justify-between font-bold text-[#111111]">
                    <span>Total</span>
                    <span>₹{cartTotal.toLocaleString('en-IN')}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Shipping & taxes calculated at checkout</p>
                </div>

                <Link to="/checkout">
                  <Button size="lg" className="w-full bg-[#111111] text-white hover:bg-[#2a2a2a]">
                    Proceed to Checkout
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>

                <Link to="/products">
                  <Button variant="ghost" size="sm" className="w-full mt-3 text-gray-500">
                    Continue Shopping
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
